import {
  Avatar,
  Badge,
  Box,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { HelpOutline, Person } from '@mui/icons-material';
import { showIntercomMessages } from 'lib/intercom';

import { MouseEvent, useState } from 'react';

import { Logo } from '@farmshare/ui-components';
import { useCognitoAuth } from '../../auth/cognito-auth-context';
import { ProducerNavModule } from './producer-nav-config';
import { ProfileMenu } from './profile-menu';
import { NotificationBell } from './notification-bell';

interface NavItemProps {
  highlighted: boolean;
  connected: boolean;
}

const NavItem = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'highlighted' && prop !== 'connected',
})<NavItemProps>(({ theme, highlighted, connected }) => ({
  width: 64,
  height: 64,
  padding: theme.spacing(0.75),
  cursor: 'pointer',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: highlighted
    ? theme.palette.background.default
    : 'transparent',
  borderRadius: 8,
  position: 'relative',
  ...(highlighted &&
    connected && {
      zIndex: 1201,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: theme.spacing(3),
        transform: 'translateX(100%)',
        backgroundColor: theme.palette.background.default,
      },
    }),
}));

interface IconRailProps {
  modules: ProducerNavModule[];
  activeModule: string | null;
  hoveredModule: string | null;
  isSecNavVisible: boolean;
  onHoverModule: (moduleId: string | null) => void;
  onClickModule: (mod: ProducerNavModule) => void;
  userName?: string;
  onLogout: () => void;
}

export function IconRail({
  modules,
  activeModule,
  hoveredModule,
  isSecNavVisible,
  onHoverModule,
  onClickModule,
  userName = undefined,
  onLogout,
}: IconRailProps) {
  const { impersonateUserId } = useCognitoAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const isActive = (id: string) => id === activeModule;
  const isHovered = (id: string) => id === hoveredModule;

  const handleAvatarClick = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const initial = userName?.charAt(0).toUpperCase();

  return (
    <Stack
      sx={{
        width: 100,
        flexShrink: 0,
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        p: 2,
        '@media print': { display: 'none' },
      }}
      onMouseLeave={() => {
        onHoverModule(null);
      }}
    >
      <Logo
        sx={{
          width: 40,
          height: 40,
          color: 'background.default',
          display: 'block',
        }}
      />
      {/* Nav modules */}
      <Stack
        spacing={1.25}
        sx={{
          flex: 1,
          width: '100%',
          alignItems: 'center',
          pt: 1,
          mt: 3,
          // Fall back to scrolling if a very short viewport still can't fit
          // every module, rather than clipping the help/avatar controls below.
          minHeight: 0,
          overflowY: 'auto',
          // Hide the scrollbar chrome; the rail stays visually clean.
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {modules.map((mod) => {
          const active = isActive(mod.id);
          const hovered = isHovered(mod.id);
          const highlighted = hovered || (active && !hoveredModule);

          return (
            <NavItem
              key={mod.id}
              direction="column"
              highlighted={highlighted}
              connected={highlighted && isSecNavVisible}
              onMouseEnter={() => onHoverModule(mod.id)}
              onClick={() => onClickModule(mod)}
              role="button"
              tabIndex={0}
              // The visible text is abbreviated to fit the rail; the
              // accessible name stays the full one.
              aria-label={mod.label}
              aria-current={active ? 'page' : undefined}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClickModule(mod);
                }
              }}
            >
              <Box
                sx={{
                  color: highlighted ? 'primary.main' : 'background.default',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& .MuiSvgIcon-root': { fontSize: 28 },
                }}
              >
                <Badge
                  badgeContent={mod.badgeCount ?? 0}
                  color="error"
                  invisible={!mod.badgeCount}
                  // Sized like the "Beta" marker below rather than MUI's
                  // default 20px circle, which on a 28px icon in a 64px rail
                  // read as a second icon competing with the first.
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.5625rem',
                      fontWeight: 700,
                      height: 15,
                      minWidth: 15,
                      padding: '0 4px',
                    },
                  }}
                  slotProps={{
                    badge: {
                      'aria-label': mod.badgeCount
                        ? `${mod.badgeCount} in ${mod.label}`
                        : undefined,
                    },
                  }}
                >
                  {/* "Beta" marker mirrors the processor Cut Sheets nav badge.
                      Nested inside the count badge so both can coexist. */}
                  <Badge
                    badgeContent="Beta"
                    color="secondary"
                    invisible={!mod.beta}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.5rem',
                        height: 14,
                        minWidth: 14,
                        padding: '0 4px',
                      },
                    }}
                  >
                    {mod.icon}
                  </Badge>
                </Badge>
              </Box>
              <Typography
                className="nav-label"
                variant="body2"
                sx={{
                  fontSize: '0.72rem',
                  fontWeight: highlighted ? 600 : 400,
                  color: highlighted ? 'primary.main' : 'background.default',
                  lineHeight: 1.2,
                  textAlign: 'center',
                  mt: 0.5,
                }}
              >
                {mod.shortLabel ?? mod.label}
              </Typography>
            </NavItem>
          );
        })}
      </Stack>

      {/* In-app notifications */}
      <NotificationBell
        sx={{
          color: 'background.default',
          mb: 1,
          '&:hover, &:active': {
            bgcolor: 'background.default',
            color: 'primary.main',
          },
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      />

      {/* Chat with us */}
      <IconButton
        onClick={() => showIntercomMessages()}
        sx={{
          color: 'background.default',
          mb: 2,
          '&:hover, &:active': {
            bgcolor: 'background.default',
            color: 'primary.main',
          },
        }}
      >
        <HelpOutline sx={{ fontSize: 32 }} />
      </IconButton>

      {/* User avatar */}
      <Avatar
        onClick={handleAvatarClick}
        role="button"
        tabIndex={0}
        aria-label="Account menu"
        aria-haspopup="true"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setAnchorEl(e.currentTarget);
          }
        }}
        data-testid="producer-avatar-menu"
        sx={{
          width: 44,
          height: 44,
          bgcolor: 'background.default',
          color: 'primary.main',
          fontSize: '1.1rem',
          fontWeight: 600,
          cursor: 'pointer',
          mb: 1,
          ...(impersonateUserId && {
            border: '2px solid',
            borderColor: 'error.main',
          }),
        }}
      >
        {initial || <Person sx={{ fontSize: 24 }} />}
      </Avatar>

      <ProfileMenu
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        onLogout={onLogout}
      />
    </Stack>
  );
}
